import { ThemeProvider } from "@mui/system";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useModalState } from "@src/lib/ui-react";
import { type ReactNode, useCallback, useEffect, useRef } from "react";

import { berlinRebrandTheme } from "../theme/theme";
import { PushNotificationPrompt } from "./components/PushNotificationPrompt";
import { useBrazePushSync } from "./hooks/useBrazePushSync";
import { usePushNotificationHandlers } from "./hooks/usePushNotificationHandlers";
import { usePushNotificationPromptState } from "./hooks/usePushNotificationPromptState";
import { usePushNotificationRegistration } from "./hooks/usePushNotificationRegistration";

interface PushNotificationProviderProps {
  children: ReactNode;
}

/**
 * Provider that handles push notification registration, event handling, and the soft prompt.
 *
 * On mount:
 * 1. Checks existing permission state (without prompting)
 * 2. If already granted → registers silently
 * 3. If prompt-eligible → shows the soft prompt after the user views the calendar
 * 4. If denied → does nothing
 *
 * Must be mounted inside the authenticated app tree (requires userId from session).
 */
export function PushNotificationProvider(props: PushNotificationProviderProps) {
  const { children } = props;

  // Get the current workplace user's ID
  const { user, admin } = useSession();
  const workplaceUserId = user?._id ?? "";

  const { permissionState, requestPermissionAndRegister } = usePushNotificationRegistration({
    workplaceUserId,
    isAdmin: admin,
  });
  usePushNotificationHandlers({ workplaceUserId });
  // TEMPORARY: Braze push sync — remove when legacy facility app is sunset
  useBrazePushSync({ workplaceUserId, isAdmin: admin });

  const { shouldShowPrompt, onDismiss, onPermissionGranted, onPermissionDenied } =
    usePushNotificationPromptState({ permissionState, workplaceUserId });

  const promptModalState = useModalState();
  const hasOpenedPrompt = useRef(false);

  // Open the prompt modal when conditions are met
  useEffect(() => {
    if (shouldShowPrompt && !hasOpenedPrompt.current) {
      hasOpenedPrompt.current = true;
      logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_SOFT_PROMPT_SHOWN);
      promptModalState.openModal();
    }
  }, [shouldShowPrompt, promptModalState]);

  const handleEnable = useCallback(async () => {
    const result = await requestPermissionAndRegister();
    if (result === "granted") {
      onPermissionGranted();
    } else if (result === "denied") {
      onPermissionDenied();
    }
  }, [onPermissionDenied, onPermissionGranted, requestPermissionAndRegister]);

  const handleDismiss = useCallback(() => {
    onDismiss();
  }, [onDismiss]);

  // Sync prompt state when permission changes externally (e.g. user denies native dialog)
  useEffect(() => {
    if (permissionState === "granted") {
      onPermissionGranted();
    } else if (permissionState === "denied") {
      onPermissionDenied();
    }
  }, [onPermissionDenied, onPermissionGranted, permissionState]);

  return (
    <>
      {children}
      <ThemeProvider theme={berlinRebrandTheme}>
        <PushNotificationPrompt
          modalState={promptModalState}
          onEnable={handleEnable}
          onDismiss={handleDismiss}
        />
      </ThemeProvider>
    </>
  );
}
